import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAccessToken } from '../auth/use-access-token.js';
import { useMeContext } from '../api/me-provider.js';
import { usePeople } from '../api/use-people.js';
import { addCartLine } from '../api/use-cart.js';
import { useCartContext } from '../api/cart-provider.js';
import { ApiError } from '../api/client.js';
import { useWaivers } from '../api/use-waivers.js';
import { fillWaiver } from '../api/fill-waiver.js';
import { useFamilyClinic } from '../api/clinics.js';
import { BookingWaivers } from '../components/booking-waivers.js';
import { CartSubmit } from '../components/cart-cta.js';
import { ClinicHead } from '../components/clinic-head.js';
import { WaitNote } from '../components/wait-note.js';

/**
 * A clinic on Book (board 2): no different to booking a lesson, only the
 * options differ (ruled 30 October). No one to one or group, several athletes,
 * Add to cart. The waiver is the hour screen's, exactly.
 *
 * A full clinic stays listed and opens here to show what it is, with nothing to
 * book. An athlete already in it is not offered again.
 */
export function ClinicRoute() {
  const { clinicId } = useParams();
  const navigate = useNavigate();
  const getToken = useAccessToken();
  const { state } = useFamilyClinic(clinicId);
  const { state: meState } = useMeContext();
  const people = usePeople();
  const { state: cartState, reload: reloadCart } = useCartContext();
  const { state: waiverState, reload: reloadWaivers } = useWaivers();

  const [chosen, setChosen] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [approved, setApproved] = useState<string[]>([]);
  const [missing, setMissing] = useState<string[]>([]);

  const back = (
    <Link className="back" to="/booking" aria-label="Back to the calendar">
      ‹
    </Link>
  );

  if (state.status === 'loading' || meState.status === 'loading') return <WaitNote className="empty">Checking…</WaitNote>;
  if (state.status === 'failed') return <p className="empty">{state.message}</p>;
  if (state.status === 'gone') {
    return (
      <section className="panel">
        <h2>That clinic has gone</h2>
        <Link className="primary" to="/booking">
          Back to the calendar
        </Link>
      </section>
    );
  }

  const clinic = state.clinic;
  const places = clinic.places_free;
  const everyone = people.state.status === 'ready' ? people.state.people : [];
  const athletes = everyone.filter((person) => !clinic.booked_ids.includes(person.client_id));

  const waiver = waiverState.status === 'ready' ? waiverState.waivers.participation : null;
  const profile = meState.status === 'ready' ? meState.me.profile : undefined;
  const guardian = profile ? `${profile.first_name} ${profile.last_name}` : '';
  const second = profile?.role === 'second';
  const needingWaiver =
    waiver && !second
      ? athletes.filter((person) => chosen.includes(person.client_id) && !person.is_account_holder && !person.waiver_current)
      : [];

  function toggle(personId: string) {
    setChosen((current) =>
      current.includes(personId)
        ? current.filter((id) => id !== personId)
        : current.length < places
          ? [...current, personId]
          : current,
    );
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving || chosen.length === 0) return;

    const unapproved = needingWaiver.map((person) => person.client_id).filter((id) => !approved.includes(id));
    setMissing(unapproved);
    if (unapproved.length > 0) return;

    setSaving(true);
    setError(null);
    try {
      for (const athleteClientId of chosen) {
        const person = needingWaiver.find((each) => each.client_id === athleteClientId);
        await addCartLine(getToken, {
          publishedSlotId: clinic.slot_id,
          athleteClientId,
          serviceId: clinic.service_id,
          capacityTierId: clinic.tier_id,
          consent:
            person && waiver
              ? {
                  waiverId: waiver.waiver_id,
                  text: fillWaiver(waiver.template, guardian, `${person.first_name} ${person.last_name}`),
                }
              : null,
        });
      }
      reloadCart();
      navigate('/cart');
    } catch (cause) {
      if (cause instanceof ApiError && cause.code === 'waiver_changed') {
        reloadWaivers();
        setApproved([]);
      }
      if (cause instanceof ApiError && cause.code === 'waiver_needed') people.reload();
      setError(messageFor(cause));
      setSaving(false);
      reloadCart();
    }
  }

  if (places <= 0) return <ClinicHead clinic={clinic} back={back} />;

  const cart = cartState.status === 'ready' ? cartState.cart : null;

  return (
    <>
      <ClinicHead clinic={clinic} back={back} />
      <form onSubmit={submit}>
        <span className="section-label">Who is coming</span>

        {people.state.status === 'failed' ? (
          <p className="empty">That did not load</p>
        ) : people.state.status === 'ready' && everyone.length === 0 ? (
          second ? (
            <p className="empty">Nobody on this account yet.</p>
          ) : (
            <p className="empty">
              Nobody on this account yet. <Link to="/account/people/new">Add a child</Link> first.
            </p>
          )
        ) : null}

        {athletes.map((person) => {
          const isChosen = chosen.includes(person.client_id);
          const full = !isChosen && chosen.length >= places;
          return (
            <label key={person.client_id} className={full ? 'choice full' : 'choice'}>
              <input type="checkbox" checked={isChosen} disabled={full} onChange={() => toggle(person.client_id)} />
              <span className="choice-text">
                <span className="name">
                  {person.first_name} {person.last_name}
                </span>
              </span>
              {full ? <span className="places">no places left</span> : null}
            </label>
          );
        })}

        {waiver && needingWaiver.length > 0 ? (
          <BookingWaivers
            people={needingWaiver}
            waiver={waiver}
            guardian={guardian}
            approved={approved}
            onChange={(personId, next) => {
              setApproved((current) => (next ? [...current, personId] : current.filter((id) => id !== personId)));
              if (next) setMissing((current) => current.filter((id) => id !== personId));
            }}
            missing={missing}
          />
        ) : null}

        {error ? <p className="state-note">{error}</p> : null}

        <CartSubmit
          label="Add to cart"
          expiresAt={cart?.expires_at}
          disabled={saving || chosen.length === 0 || waiverState.status === 'loading'}
          busy={saving}
          working="Adding…"
        />
      </form>
    </>
  );
}

function messageFor(cause: unknown): string {
  if (!(cause instanceof ApiError)) return 'That did not save. Try again.';
  if (cause.code === 'slot_not_bookable') return 'That clinic has just gone.';
  if (cause.code === 'already_in_cart') return 'That athlete is already in the cart for this clinic.';
  if (cause.code === 'athlete_not_linked') return 'That athlete is not on your account.';
  if (cause.code === 'waiver_changed') return 'The wording has changed. Read it again and approve.';
  if (cause.code === 'waiver_needed') return 'Approve before adding.';
  return 'That did not save. Try again.';
}
